<script setup lang="ts">
import { ref, computed, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { AIDisclosureParagraphType } from '@/services/ai-disclosure/type/ai-disclosure.enum';
import type { DisclosureResult } from '@/services/ai-disclosure/type/ai-disclosure.type';
import _ from 'lodash';
import IconStar from '@/presentation/components/icons/IconStar.vue';
import '@patsnap-ui/icon/assets/solid/edit.svg';
import '@patsnap-ui/icon/assets/solid/EyeOff.svg';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
import DisclosureTextEditor from './DisclosureTextEditor.vue';
import { useResizeObserver } from '@vueuse/core'

const { t } = useI18n();

const props = defineProps<{
    paragraph: DisclosureResult;
    noEdit: boolean;
}>();

const emits = defineEmits<{
    (event: 'resize'): void;
    (event: 'selectionChange', range: Range, paragraphType: AIDisclosureParagraphType): void;
    (event: 'clickOptimizationBtn', dom: HTMLElement, paragraphType: AIDisclosureParagraphType): void;
    (event: 'updateContent', paragraphType: AIDisclosureParagraphType, content: string): void;
    (event: 'visibleChange', paragraph: DisclosureResult): void;
}>();

const isEditing = ref(false);
const isCheckingSensitiveWord = ref(false);

const isLevelOneTitle = computed(() => !props.paragraph.parentTitle);
const hasOptimization = computed(() => props.paragraph.type !== AIDisclosureParagraphType.TECH_FIELD);
const hasSelectionAttribute = computed(() => props.paragraph.type !== AIDisclosureParagraphType.TECH_FIELD && props.paragraph.content);
const showEditBar = computed(() => {
    const AIDisclosureParagraphTypeValues = Object.values(AIDisclosureParagraphType);
    return AIDisclosureParagraphTypeValues.includes(props.paragraph.type) && !isEditing.value && !props.noEdit;
});
const showHover = computed(() => !isEditing.value && showEditBar.value);

const debounceHandleResize = _.debounce(() => {
    emits('resize');
}, 100);

function handleSelectionchange() {
    const selection = window.getSelection();
    if (selection.toString() && props.paragraph.paragraphType) {
        const range = selection.getRangeAt(0);
        emits('selectionChange', range, props.paragraph.paragraphType);
    }
}

function handleClickOptimizationBtn() {
    const dom = document.getElementById(`disclosure-paragraph-optimization-${props.paragraph.type}`);
    if (dom && props.paragraph.paragraphType) {
        emits('clickOptimizationBtn', dom, props.paragraph.paragraphType);
    }
}

function handelEdit() {
    isEditing.value = true;
}

function handelEditCancel() {
    isEditing.value = false;
}

function handleEditSubmit(newText: string) {
    if (props.paragraph.paragraphType) {
        emits('updateContent', props.paragraph.paragraphType, newText);

        isEditing.value = false;
    }
}

function handleParagraphVisibleChange() {
    emits('visibleChange', props.paragraph);
}

let container = null;

watch(
    () => [props.paragraph.content, props.noEdit],
    () => {
        if (hasSelectionAttribute.value && !props.noEdit) {
            nextTick(() => {
                if (container) {
                    container.removeEventListener('mouseup', handleSelectionchange);
                }
                container = document.getElementById(props.paragraph.type);
                container.addEventListener('mouseup', handleSelectionchange);
            });
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    if (hasSelectionAttribute.value && container) {
        container.removeEventListener('mouseup', handleSelectionchange);
    }

    debounceHandleResize.cancel();
});

const containerRef = ref<HTMLElement | null>(null);
useResizeObserver(containerRef, debounceHandleResize)

</script>

<template>
    <div
        :id="`disclosure-paragraph-${paragraph.type}`"
        ref="containerRef"
        class="disclosure-paragraph group relative mt-8 w-[640px] cursor-pointer overflow-y-hidden rounded-lg p-3"
        :class="{'hover:bg-blue-40/50': showHover }"
    >
        <div class="flex items-center justify-between">
            <div
                :class="[
                    'mb-2 font-semibold text-black',
                    isLevelOneTitle ? 'text-xl' : 'text-base'
                ]"
            >
                {{ paragraph.title }}
            </div>

            <div v-if="showEditBar" class="paragraph-edit-bar hidden group-hover:flex">
                <button
                    v-if="hasOptimization"
                    :id="`disclosure-paragraph-optimization-${paragraph.type}`"
                    class="th-btn th-btn-plain th-btn-sm"
                    @click="handleClickOptimizationBtn"
                >
                    <IconStar
                        class="th-btn-icon h-6 w-6 !fill-grey-450"
                    />

                    <span>
                        {{ t('aiDisclosure.optimization') }}
                    </span>
                </button>

                <button class="th-btn th-btn-plain th-btn-sm" @click="handelEdit">
                    <SvgIcon
                        class="th-btn-icon mr-1 h-4 w-4"
                        icon="SolidEdit"
                    />

                    <span>
                        {{ t('aiDisclosureGlobal.globalBtn.edit') }}
                    </span>
                </button>

                <button class="th-btn th-btn-plain th-btn-sm" @click="handleParagraphVisibleChange">
                    <SvgIcon
                        class="th-btn-icon mr-1 h-4 w-4"
                        icon="SolidEyeOff"
                    />

                    <span>
                        {{ t('aiDisclosureGlobal.globalBtn.hidden') }}
                    </span>
                </button>
            </div>
        </div>

        <DisclosureTextEditor
            v-if="isEditing"
            :defaultText="paragraph.content"
            :isLoadingSubmit="isCheckingSensitiveWord"
            @cancel="handelEditCancel"
            @submit="handleEditSubmit"
        />

        <div
            v-else-if="paragraph.content"
            :id="paragraph.type"
            class="whitespace-pre-line text-base !leading-8 text-black"
        >
            {{ paragraph.content }}
        </div>
    </div>
</template>
